import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PhoneInput from '../../components/feature/PhoneInput';

export default function LoginPage() {
  // Base path pour les URLs (gère les déploiements sur sous-chemin)
  const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';

  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  // Flag d'erreur réseau : différencie "serveur injoignable" de "identifiants incorrects"
  const [isNetworkError, setIsNetworkError] = useState(false);
  
  // États pour la vérification email
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailResendTimer, setEmailResendTimer] = useState(0);
  
  // États pour la vérification téléphone (2FA SMS)
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);
  const [phoneResendTimer, setPhoneResendTimer] = useState(0);
  
  // Email 2FA (alias du domaine) du membre du personnel
  const [verificationEmail, setVerificationEmail] = useState('');
  const [show2faSetupRequired, setShow2faSetupRequired] = useState(false);
  const [showSecurityResetBanner, setShowSecurityResetBanner] = useState(false);
  
  // États pour la configuration de l'email personnel (réinitialisation sécurité médecins)
  const [showSecuritySetup, setShowSecuritySetup] = useState(false);
  const [securitySetupStep, setSecuritySetupStep] = useState<'enterEmail' | 'enterCode'>('enterEmail');
  const [securityPersonalEmail, setSecurityPersonalEmail] = useState('');
  const [securityCode, setSecurityCode] = useState('');
  const [securityCodeSent, setSecurityCodeSent] = useState(false);
  const [securityResendTimer, setSecurityResendTimer] = useState(0);
  const [securityLoginEmail, setSecurityLoginEmail] = useState('');
  const [securityLoading, setSecurityLoading] = useState(false);
  const [securityError, setSecurityError] = useState('');
  
  // État pour Cloudflare Turnstile
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [turnstileError, setTurnstileError] = useState(false);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const turnstileMountedRef = useRef(false);
  
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const { signIn, user, role, loading: authLoading, signInWithGoogle, rolesLoading, refreshRoles } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Lire le message d'info passé via le state (ex: compte déjà existant)
  const [infoMessage, setInfoMessage] = useState('');

  // 🔒 Fonction pour effacer TOUTES les données sensibles du formulaire
  const clearAllFormData = () => {
    setEmail('');
    setPhone('');
    setPassword('');
    setLoginEmail('');
    setShowPassword(false);
    setTurnstileToken('');
    setError('');
    setInfoMessage('');
    setVerificationEmail('');
    setShow2faSetupRequired(false);
    // Forcer le reset des champs DOM pour contourner le bfcache
    if (emailInputRef.current) emailInputRef.current.value = '';
    if (passwordInputRef.current) passwordInputRef.current.value = '';
  };

  // 🔒 Détection du bfcache (back-forward cache) : quand l'utilisateur revient
  // sur la page via le bouton retour du navigateur, on efface tout
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // La page a été restaurée depuis le bfcache → effacer les credentials
        clearAllFormData();
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      // Nettoyage au démontage du composant
      clearAllFormData();
    };
  }, []);
  
  // 🛡️ Bandeau de réinitialisation sécurité médecins
  useEffect(() => {
    const checkSecurityReset = async () => {
      try {
        const { data } = await supabase
          .from('system_config')
          .select('config_value')
          .eq('config_key', 'medecin_security_reset')
          .maybeSingle();
        if (data?.config_value?.active === true) {
          const armedAt = data.config_value.armed_at ? new Date(data.config_value.armed_at).getTime() : 0;
          if (Date.now() - armedAt < 30 * 24 * 3600 * 1000) {
            setShowSecurityResetBanner(true);
          }
        }
      } catch {
        // Silencieux
      }
    };
    checkSecurityReset();
  }, []);

  useEffect(() => {
    const state = location.state as { infoMessage?: string; message?: string } | null;
    if (state?.infoMessage) {
      setInfoMessage(state.infoMessage);
      // Nettoyer le state pour éviter de réafficher après refresh
      window.history.replaceState({}, document.title);
    } else if (state?.message) {
      setInfoMessage(state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Détecter les erreurs OAuth dans l'URL au chargement (retour de connexion Google échoué)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const oauthError = urlParams.get('error');
    const oauthErrorDesc = urlParams.get('error_description');
    
    if (oauthError) {
      // Nettoyer l'URL pour enlever les params d'erreur
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, cleanUrl);
      
      if (oauthError === 'access_denied') {
        setError('Vous avez refusé l\'accès à votre compte Google.');
      } else if (oauthErrorDesc?.toLowerCase().includes('provider') || oauthErrorDesc?.toLowerCase().includes('not enabled')) {
        setError('La connexion Google n\'est pas encore configurée. Veuillez contacter l\'administrateur du site.');
      } else {
        setError(`Erreur lors de la connexion Google : ${oauthErrorDesc || oauthError}`);
      }
    }
  }, []);

  // 🛡️ Rediriger les utilisateurs déjà connectés vers leur dashboard
  useEffect(() => {
    if (!authLoading && !rolesLoading && user && role) {
      // Effacer les données sensibles avant de rediriger
      clearAllFormData();
      const dashboardRoute = getDashboardRouteForRole(role);
      navigate(dashboardRoute, { replace: true });
    }
  }, [authLoading, rolesLoading, user, role, navigate]);

  // Helper pour obtenir la route dashboard selon le rôle
  const getDashboardRouteForRole = (userRole: string): string => {
    switch (userRole) {
      case 'super_admin': return '/admin';
      case 'medecin': return '/medecin/rendez-vous';
      case 'secretaire': return '/secretaire/dashboard';
      case 'patient': return '/patient/rendez-vous';
      case 'laborantin': return '/laborantin/dashboard';
      case 'sage_femme': return '/sage-femme/dashboard';
      case 'infirmier': return '/infirmier/dashboard';
      case 'comptable': return '/comptable/dashboard';
      default: return '/';
    }
  };

  // Gérer le retour de l'OAuth Google (après redirection)
  useEffect(() => {
    if (!authLoading && !rolesLoading && user) {
      // Vérifier si on revient d'une connexion Google
      const googleAction = sessionStorage.getItem('google_auth_action');
      if (googleAction === 'login') {
        sessionStorage.removeItem('google_auth_action');
        
        if (role) {
          // 🔒 Google Sign-In réservé aux patients uniquement
          if (role !== 'patient') {
            setError('La connexion Google est réservée aux patients. Si vous êtes un membre du personnel, veuillez vous connecter avec votre email et mot de passe.');
            supabase.auth.signOut();
            setLoading(false);
            return;
          }

          // Créer la session utilisateur avant de rediriger
          createSessionForUser(role).then(() => {
            navigate('/patient/rendez-vous', { replace: true });
          });
        } else {
          handleGoogleAccountLinking(user);
        }
      }
    }
  }, [user, role, authLoading, rolesLoading, navigate]);

  // Charger Cloudflare Turnstile
  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const renderTurnstile = () => {
      if (cancelled || !turnstileContainerRef.current) return;
      // Éviter le double render (StrictMode / remount)
      if (turnstileMountedRef.current) return;

      try {
        const widgetId = (window.turnstile as any).render(turnstileContainerRef.current, {
          sitekey: '0x4AAAAAACFuWJkMdzmwzN09',
          callback: (token: string) => {
            if (!cancelled) {
              setTurnstileToken(token);
            }
          },
          'expired-callback': () => {
            if (!cancelled) {
              setTurnstileToken('');
              // Forcer le reset visuel du widget pour que l'utilisateur voie qu'il doit recommencer
              try {
                if (turnstileWidgetIdRef.current && window.turnstile) {
                  (window.turnstile as any).reset(turnstileWidgetIdRef.current);
                }
              } catch (e) { /* ignore */ }
            }
          },
          'error-callback': () => {
            if (!cancelled) {
              setTurnstileToken('');
              setTurnstileError(true);
              // Forcer le reset visuel
              try {
                if (turnstileWidgetIdRef.current && window.turnstile) {
                  (window.turnstile as any).reset(turnstileWidgetIdRef.current);
                }
              } catch (e) { /* ignore */ }
            }
          },
          'timeout-callback': () => {
            // L'utilisateur a mis trop de temps → reset complet
            if (!cancelled) {
              setTurnstileToken('');
              setTurnstileReady(false);
              turnstileMountedRef.current = false;
              turnstileWidgetIdRef.current = null;
              setTurnstileKey(prev => prev + 1);
            }
          },
        });
        turnstileWidgetIdRef.current = widgetId || null;
        turnstileMountedRef.current = true;
        if (!cancelled) {
          setTurnstileReady(true);
          setTurnstileError(false);
        }
      } catch (err) {
        if (!cancelled) {
          setTurnstileError(true);
        }
      }
    };

    // Essayer de charger Turnstile immédiatement, ou attendre le script
    if (window.turnstile) {
      // Rendu immédiat — le script est déjà en cache, le DOM est prêt
      renderTurnstile();
      return () => {
        cancelled = true;
        if (turnstileWidgetIdRef.current && window.turnstile) {
          try { (window.turnstile as any).remove(turnstileWidgetIdRef.current); } catch (e) { /* ignore */ }
          turnstileWidgetIdRef.current = null;
        }
        turnstileMountedRef.current = false;
      };
    }

    // Script pas encore chargé → polling avec timeout
    const checkInterval = setInterval(() => {
      if (cancelled) {
        clearInterval(checkInterval);
        return;
      }
      if (window.turnstile) {
        clearInterval(checkInterval);
        clearTimeout(timeoutId);
        renderTurnstile();
      }
    }, 200);

    // Timeout de sécurité : si le script ne charge pas en 12 secondes, afficher erreur
    timeoutId = setTimeout(() => {
      clearInterval(checkInterval);
      if (!cancelled && !turnstileMountedRef.current) {
        setTurnstileError(true);
      }
    }, 12000);

    return () => {
      cancelled = true;
      clearInterval(checkInterval);
      clearTimeout(timeoutId);
      if (turnstileWidgetIdRef.current && window.turnstile) {
        try { (window.turnstile as any).remove(turnstileWidgetIdRef.current); } catch (e) { /* ignore */ }
        turnstileWidgetIdRef.current = null;
      }
      turnstileMountedRef.current = false;
    };
  }, [turnstileKey]);

  // Rafraîchir le token Turnstile (les tokens sont à usage unique : après une
  // tentative échouée, on réarme le widget pour obtenir un nouveau jeton sans
  // que l'utilisateur ait à re-résoudre le challenge manuellement)
  const refreshTurnstile = () => {
    setTurnstileToken('');
    turnstileMountedRef.current = false;
    turnstileWidgetIdRef.current = null;
    setTurnstileReady(false);
    setTurnstileKey(prev => prev + 1);
  };

  // Envoyer le code de vérification par email
  const sendEmailVerificationCode = async () => {
    setError('');
    setIsNetworkError(false);
    setLoading(true);

    try {
      let response: Response;
      try {
        response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-verification-code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            identifier: verificationEmail || email,
            type: 'email',
            action: 'login'
          }),
        });
      } catch (netErr: any) {
        throw new Error('NETWORK_ERROR|Impossible de contacter le serveur pour l\'envoi du code. Vérifiez votre connexion internet.');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de l\'envoi du code');
      }

      setEmailCodeSent(true);
      setShowEmailVerification(true);
      setLoading(false);
      
      // Démarrer le timer de 60 secondes
      setEmailResendTimer(60);
      const interval = setInterval(() => {
        setEmailResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

    } catch (err: any) {
      const msg = err.message || '';
      if (msg.startsWith('NETWORK_ERROR|')) {
        setError(msg.replace('NETWORK_ERROR|', ''));
        setIsNetworkError(true);
      } else {
        setError(msg || 'Erreur lors de l\'envoi du code');
      }
      setLoading(false);
    }
  };

  // Vérifier le code email et finaliser la connexion
  const verifyEmailCodeAndLogin = async () => {
    setError('');
    setIsNetworkError(false);
    setLoading(true);

    try {
      let response: Response;
      try {
        response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/verify-email-code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            identifier: verificationEmail || email,
            code: emailVerificationCode,
            action: 'login'
          }),
        });
      } catch (netErr: any) {
        throw new Error('NETWORK_ERROR|Impossible de vérifier le code. Vérifiez votre connexion internet.');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Code invalide');
      }

      const signInResult = await signIn(loginEmail || email, password, 'email');
      if (!signInResult || !signInResult.role) {
        setError('Erreur lors du chargement de votre profil. Veuillez réessayer.');
        setLoading(false);
        return;
      }

      await navigateAfterLogin(signInResult.role);

    } catch (err: any) {
      const msg = err.message || '';
      if (msg.startsWith('NETWORK_ERROR|')) {
        setError(msg.replace('NETWORK_ERROR|', ''));
        setIsNetworkError(true);
      } else {
        setError(msg || 'Code invalide ou expiré');
      }
      setLoading(false);
    }
  };

  // Helper : normaliser le numéro de téléphone en email local
  const normalizePhoneToEmail = (rawPhone: string): { normalizedPhone: string; phoneEmail: string } => {
    let normalizedPhone = rawPhone.trim().replace(/\s+/g, '');
    if (normalizedPhone.startsWith('0')) {
      normalizedPhone = '+229' + normalizedPhone.substring(1);
    }
    if (!normalizedPhone.startsWith('+')) {
      normalizedPhone = '+229' + normalizedPhone;
    }
    const phoneEmail = `phone_${normalizedPhone.replace('+', '')}@cliniquesaintefamille.local`;
    return { normalizedPhone, phoneEmail };
  };

  // Envoyer le code de vérification par SMS (2FA téléphone)
  const sendPhoneVerificationCode = async () => {
    setError('');
    setIsNetworkError(false);
    setLoading(true);

    try {
      const { normalizedPhone } = normalizePhoneToEmail(phone);

      // Validation : vérifier que le numéro est complet
      const digitsOnly = normalizedPhone.replace(/\D/g, '');
      if (digitsOnly.length < 8) {
        throw new Error('Numéro de téléphone incomplet. Veuillez saisir un numéro valide.');
      }

      let response: Response;
      try {
        response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-phone-otp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            phone: normalizedPhone,
            action: 'login',
            turnstileToken
          }),
        });
      } catch (netErr: any) {
        throw new Error('NETWORK_ERROR|Impossible de contacter le serveur pour l\'envoi du SMS. Vérifiez votre connexion internet.');
      }

      const data = await response.json();

      if (!response.ok) {
        if (data.error && data.error.includes('SMS_INACTIVE|')) {
          const userMessage = data.error.split('SMS_INACTIVE|')[1];
          throw new Error(userMessage);
        }
        throw new Error(data.error || 'Erreur lors de l\'envoi du code');
      }

      setPhoneCodeSent(true);
      setShowPhoneVerification(true);
      setLoading(false);

      // Démarrer le timer de 60 secondes
      setPhoneResendTimer(60);
      const interval = setInterval(() => {
        setPhoneResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

    } catch (err: any) {
      const msg = err.message || '';
      if (msg.startsWith('NETWORK_ERROR|')) {
        setError(msg.replace('NETWORK_ERROR|', ''));
        setIsNetworkError(true);
      } else {
        setError(msg || 'Erreur lors de l\'envoi du code');
      }
      setLoading(false);
    }
  };

  // Vérifier le code SMS et finaliser la connexion
  const verifyPhoneCodeAndLogin = async () => {
    setError('');
    setIsNetworkError(false);
    setLoading(true);

    try {
      const { normalizedPhone } = normalizePhoneToEmail(phone);

      let response: Response;
      try {
        response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/verify-phone-otp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            phone: normalizedPhone,
            code: phoneVerificationCode,
          }),
        });
      } catch (netErr: any) {
        throw new Error('NETWORK_ERROR|Impossible de vérifier le code SMS. Vérifiez votre connexion internet.');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Code invalide');
      }

      const signInResult = await signIn(loginEmail, password, 'email');
      if (!signInResult || !signInResult.role) {
        setError('Erreur lors du chargement de votre profil. Veuillez réessayer.');
        setLoading(false);
        return;
      }

      await navigateAfterLogin(signInResult.role);

    } catch (err: any) {
      const msg = err.message || '';
      if (msg.startsWith('NETWORK_ERROR|')) {
        setError(msg.replace('NETWORK_ERROR|', ''));
        setIsNetworkError(true);
      } else {
        setError(msg || 'Code invalide ou expiré');
      }
      setLoading(false);
    }
  };

  // Envoyer le code de vérification à l'email personnel (réinitialisation sécurité)
  const sendSecuritySetupCode = async () => {
    setSecurityError('');
    if (!securityPersonalEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(securityPersonalEmail)) {
      setSecurityError('Veuillez saisir une adresse email valide.');
      return;
    }
    setSecurityLoading(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-verification-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: securityPersonalEmail.trim().toLowerCase(), type: 'email', action: 'medecin_2fa_setup' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur lors de l\'envoi du code');
      setSecurityCodeSent(true);
      setSecuritySetupStep('enterCode');
      setSecurityResendTimer(60);
      const interval = setInterval(() => {
        setSecurityResendTimer((prev) => {
          if (prev <= 1) { clearInterval(interval); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      setSecurityError(err.message || 'Erreur lors de l\'envoi du code');
    } finally {
      setSecurityLoading(false);
    }
  };

  // Vérifier le code et lier l'email personnel, puis se connecter
  const verifySecuritySetupCode = async () => {
    setSecurityError('');
    if (securityCode.length !== 6) {
      setSecurityError('Veuillez saisir le code à 6 chiffres.');
      return;
    }
    setSecurityLoading(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/link-medecin-2fa-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          loginEmail: securityLoginEmail || email,
          personalEmail: securityPersonalEmail.trim().toLowerCase(),
          code: securityCode,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Code invalide');

      // Email personnel lié → fermer le parcours et connecter le médecin
      setShowSecuritySetup(false);
      setLoading(true);
      const signInResult = await signIn(securityLoginEmail || email, password, 'email');
      if (!signInResult || !signInResult.role) {
        setError('Erreur lors du chargement de votre profil. Veuillez réessayer.');
        setLoading(false);
        return;
      }
      await navigateAfterLogin(signInResult.role);
    } catch (err: any) {
      setSecurityError(err.message || 'Erreur lors de la vérification');
      setSecurityLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setIsNetworkError(false);
    setLoading(true);
    try {
      sessionStorage.setItem('google_auth_action', 'login');
      // Inclure le pathPrefix pour supporter les déploiements sur sous-chemin
      const redirectUrl = `${window.location.origin}${pathPrefix}/login`;
      await signInWithGoogle(redirectUrl);
    } catch (err: any) {
      console.error('[Login] Erreur Google:', err);
      const errMsg = err.message || '';
      if (errMsg.includes('Failed to fetch') || errMsg.includes('network') || errMsg.includes('fetch')) {
        setError('Impossible de contacter le service Google. Vérifiez votre connexion internet.');
        setIsNetworkError(true);
      } else if (errMsg.includes('provider') || errMsg.includes('not configured') || errMsg.includes('Enable') || errMsg.includes('enabled')) {
        setError('La connexion Google n\'est pas encore configurée. Veuillez contacter l\'administrateur du site.');
      } else {
        setError('Erreur lors de la connexion avec Google. Veuillez réessayer.');
      }
      setLoading(false);
    }
  };

  // Lier un compte Google à un profil patient existant (quand le rôle n'est pas trouvé après OAuth)
  const handleGoogleAccountLinking = async (googleUser: any) => {
    const googleEmail = googleUser?.email;
    if (!googleEmail) {
      setError('Impossible de récupérer votre email Google. Veuillez réessayer.');
      await supabase.auth.signOut();
      return;
    }

    try {
      setLoading(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      const linkRes = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/link-google-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ newUserId: googleUser.id, email: googleEmail }),
      });
      const linkData = await linkRes.json();

      if (linkData.success && linkData.role) {
        // Rafraîchir les rôles dans AuthContext AVANT de naviguer
        const { primaryRole: refreshedRole } = await refreshRoles();

        if (!refreshedRole) {
          setError('La liaison a réussi mais le chargement du profil a échoué. Veuillez vous reconnecter.');
          await supabase.auth.signOut();
          setLoading(false);
          return;
        }

        // 🔒 Google uniquement pour les patients : si refreshRoles retourne autre chose, bloquer
        if (refreshedRole !== 'patient') {
          setError('La connexion Google est réservée aux patients. Si vous êtes un membre du personnel, veuillez vous connecter avec votre email et mot de passe.');
          await supabase.auth.signOut();
          setLoading(false);
          return;
        }

        await createSessionForUser(refreshedRole);
        navigate('/patient/rendez-vous', { replace: true });
      } else {
        setError(linkData.error || 'La connexion Google est réservée aux patients. Aucun compte patient trouvé avec cet email.');
        await supabase.auth.signOut();
      }
    } catch (linkErr: any) {
      const msg = linkErr?.message || '';
      if (msg.includes('fetch') || msg.includes('Failed to fetch') || msg.includes('network')) {
        setError('Impossible de contacter le serveur. Vérifiez votre connexion internet.');
        setIsNetworkError(true);
      } else {
        setError('Erreur lors de la liaison du compte Google. Veuillez réessayer.');
      }
      await supabase.auth.signOut();
    } finally {
      setLoading(false);
    }
  };

  // Générer une empreinte unique de l'appareil (pour la confiance)
  const getDeviceFingerprint = (): string => {
    const components = [
      navigator.userAgent,
      navigator.platform,
      navigator.language,
      `${window.screen.width}x${window.screen.height}`,
      navigator.hardwareConcurrency || 'unknown',
      new Intl.DateTimeFormat().resolvedOptions().timeZone,
    ];
    // Simple hash maison (pas besoin de crypto pour ça)
    let hash = 0;
    const str = components.join('|');
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash |= 0;
    }
    return `fp_${Math.abs(hash).toString(36)}`;
  };

  // Enregistrer cet appareil comme appareil de confiance
  const trustCurrentDevice = async (userId: string) => {
    const fingerprint = getDeviceFingerprint();
    try {
      await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/trust-device`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, deviceFingerprint: fingerprint, days: 30 }),
      });
    } catch (err) {
      // Silencieux
    }
  };

  // Créer une session utilisateur (réutilisable par tous les flux de connexion)
  const createSessionForUser = async (userRole: string): Promise<boolean> => {
    let ipAddress = null;
    try {
      // 🔒 Délai maximal de 3s : ipify est un service externe optionnel qui ne doit
      // JAMAIS bloquer la connexion s'il est lent ou inaccessible sur le réseau.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      const ipResponse = await fetch('https://api.ipify.org?format=json', {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      const ipData = await ipResponse.json();
      ipAddress = ipData.ip;
    } catch (error) {
      // Silencieux
    }

    const userAgent = navigator.userAgent;
    const deviceInfo = {
      platform: navigator.platform,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
    };

    const { data: authData } = await supabase.auth.getUser();
    if (authData.user) {
      await trustCurrentDevice(authData.user.id);

      try {
        const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-user-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: authData.user.id,
            userRole: userRole,
            ipAddress,
            userAgent,
            deviceInfo
          }),
        });
        const sessionData = await response.json();
        if (sessionData?.sessionId) {
          localStorage.setItem('current_session_id', sessionData.sessionId);
          return true;
        }
      } catch (err) {
        // Silencieux
      }
    }
    return false;
  };

  // Navigation après connexion réussie (commune aux deux flux)
  const navigateAfterLogin = async (userRole: string) => {
    // 🔒 Effacer immédiatement les données sensibles avant toute navigation
    clearAllFormData();
    // 🔒 La création de session en BDD se fait en arrière-plan : elle ne doit
    // JAMAIS bloquer la navigation vers le tableau de bord. C'était la cause du
    // blocage (page de login figée) quand ipify/create-user-session était lent.
    createSessionForUser(userRole).catch(() => {});

    // Vérifier si le patient doit changer son mot de passe
    if (userRole === 'patient') {
      const { data: pwdCheck } = await supabase.functions.invoke('check-password-requirement');
      
      if (pwdCheck?.mustChangePassword) {
        if (pwdCheck.deadlinePassed) {
          setShowBlockedModal(true);
          setLoading(false);
          return;
        } else {
          navigate('/change-password', { replace: true });
          return;
        }
      }
    }
    
    if (userRole === 'super_admin') {
      navigate('/admin', { replace: true });
    } else if (userRole === 'medecin') {
      // 🔐 Vérifier si le médecin doit changer son mot de passe (réinitialisation sécurité)
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser) {
          const { data: medData } = await supabase
            .from('medecins')
            .select('must_change_password')
            .eq('user_id', authUser.id)
            .maybeSingle();
          if (medData?.must_change_password) {
            navigate('/change-password-medecin', { replace: true });
            return;
          }
        }
      } catch (e) { /* ignore */ }
      navigate('/medecin/rendez-vous', { replace: true });
    } else if (userRole === 'secretaire') {
      navigate('/secretaire/dashboard', { replace: true });
    } else if (userRole === 'patient') {
      navigate('/patient/rendez-vous', { replace: true });
    } else if (userRole === 'laborantin') {
      navigate('/laborantin/dashboard', { replace: true });
    } else if (userRole === 'sage_femme') {
      navigate('/sage-femme/dashboard', { replace: true });
    } else if (userRole === 'infirmier') {
      navigate('/infirmier/dashboard', { replace: true });
    } else if (userRole === 'comptable') {
      navigate('/comptable/dashboard', { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setError('');
    setIsNetworkError(false);

    // Vérifier Cloudflare Turnstile
    if (!turnstileToken) {
      setError('Veuillez compléter la vérification anti-robot');
      return;
    }

    // Si connexion par email : vérifier credentials + appareil de confiance
    if (loginMethod === 'email') {
      setLoading(true);
      try {
        const deviceFingerprint = getDeviceFingerprint();

        // Vérifier les credentials via l'edge function (ne crée PAS de session)
        let verifyRes: Response;
        try {
          verifyRes = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/verify-login-credentials`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ identifier: email, password, deviceFingerprint, method: 'email', turnstileToken }),
          });
        } catch (fetchErr: any) {
          // Erreur réseau brute : serveur injoignable
          console.error('[Login] Échec réseau verify-login-credentials:', fetchErr?.message || fetchErr);
          throw new Error('NETWORK_ERROR|Impossible de contacter le serveur d\'authentification. Vérifiez votre connexion internet.');
        }

        const verifyData = await verifyRes.json();

        // 🔐 Réinitialisation sécurité : le médecin doit configurer son email personnel
        if (verifyData.requiresSecuritySetup) {
          setSecurityLoginEmail(verifyData.loginEmail || email);
          setShowSecuritySetup(true);
          setSecuritySetupStep('enterEmail');
          setSecurityPersonalEmail('');
          setSecurityCode('');
          setSecurityCodeSent(false);
          setSecurityError('');
          setLoading(false);
          return;
        }

        if (!verifyRes.ok || !verifyData.valid) {
          // 2FA obligatoire mais email 2FA non configuré (personnel)
          if (verifyData.requires2faSetup) {
            setShow2faSetupRequired(true);
            setLoading(false);
            return;
          }
          const errMsg = verifyData.error || 'Email ou mot de passe incorrect';
          // Rafraîchir le token Turnstile (à usage unique) pour permettre une nouvelle tentative
          refreshTurnstile();
          throw new Error(errMsg);
        }

        // Stocker l'email de connexion retourné (utile pour la connexion croisée)
        const resolvedLoginEmail = verifyData.loginEmail || email;
        setLoginEmail(resolvedLoginEmail);
        // Email 2FA (alias) : le code part sur cet email pour le personnel
        setVerificationEmail(verifyData.verificationEmail || resolvedLoginEmail);

        // Appareil de confiance ? On saute le code 2FA !
        if (verifyData.deviceTrusted) {
          let signInResult: { role: string } | undefined;
          try {
            signInResult = await signIn(resolvedLoginEmail, password, 'email');
          } catch (signInErr: any) {
            console.error('[Login] Échec signIn après verify:', signInErr?.message || signInErr);
            if (signInErr?.message?.includes('fetch') || signInErr?.message?.includes('network') || signInErr?.message?.includes('Failed to fetch')) {
              throw new Error('NETWORK_ERROR|Impossible de finaliser la connexion. Le service d\'authentification est momentanément indisponible.');
            }
            throw signInErr;
          }
          if (!signInResult || !signInResult.role) {
            setError('Erreur lors du chargement de votre profil. Veuillez réessayer.');
            setLoading(false);
            return;
          }
          await navigateAfterLogin(signInResult.role);
          return;
        }

        // Appareil non reconnu → envoyer le code de vérification
        await sendEmailVerificationCode();
      } catch (err: any) {
        const msg = err.message || '';
        if (msg.startsWith('NETWORK_ERROR|')) {
          setError(msg.replace('NETWORK_ERROR|', ''));
          setIsNetworkError(true);
        } else {
          setError(msg || 'Une erreur est survenue lors de la connexion. Veuillez réessayer.');
        }
        setLoading(false);
      }
    } else {
      // Connexion par téléphone avec 2FA SMS
      setLoading(true);
      try {
        const deviceFingerprint = getDeviceFingerprint();

        // Vérifier les credentials via l'edge function (ne crée PAS de session)
        let verifyRes: Response;
        try {
          verifyRes = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/verify-login-credentials`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ identifier: phone, password, deviceFingerprint, method: 'phone', turnstileToken }),
          });
        } catch (fetchErr: any) {
          console.error('[Login] Échec réseau verify-login-credentials (phone):', fetchErr?.message || fetchErr);
          throw new Error('NETWORK_ERROR|Impossible de contacter le serveur d\'authentification. Vérifiez votre connexion internet.');
        }

        const verifyData = await verifyRes.json();

        if (!verifyRes.ok || !verifyData.valid) {
          if (verifyData.requires2faSetup) {
            setShow2faSetupRequired(true);
            setLoading(false);
            return;
          }
          const errMsg = verifyData.error || 'Numéro de téléphone ou mot de passe incorrect';
          // Rafraîchir le token Turnstile (à usage unique) pour permettre une nouvelle tentative
          refreshTurnstile();
          throw new Error(errMsg);
        }

        // Stocker l'email de connexion retourné (peut être un email réel ou factice)
        const resolvedLoginEmail = verifyData.loginEmail;
        if (!resolvedLoginEmail) {
          throw new Error('Impossible de trouver le compte associé à ce numéro');
        }
        setLoginEmail(resolvedLoginEmail);

        // Appareil de confiance ? On saute le code 2FA !
        if (verifyData.deviceTrusted) {
          let signInResult: { role: string } | undefined;
          try {
            signInResult = await signIn(resolvedLoginEmail, password, 'email');
          } catch (signInErr: any) {
            console.error('[Login] Échec signIn après verify (phone):', signInErr?.message || signInErr);
            if (signInErr?.message?.includes('fetch') || signInErr?.message?.includes('network') || signInErr?.message?.includes('Failed to fetch')) {
              throw new Error('NETWORK_ERROR|Impossible de finaliser la connexion. Le service d\'authentification est momentanément indisponible.');
            }
            throw signInErr;
          }
          if (!signInResult || !signInResult.role) {
            setError('Erreur lors du chargement de votre profil. Veuillez réessayer.');
            setLoading(false);
            return;
          }
          await navigateAfterLogin(signInResult.role);
          return;
        }

        // Appareil non reconnu → envoyer le code de vérification par SMS
        await sendPhoneVerificationCode();
      } catch (err: any) {
        const msg = err.message || '';
        if (msg.startsWith('NETWORK_ERROR|')) {
          setError(msg.replace('NETWORK_ERROR|', ''));
          setIsNetworkError(true);
        } else {
          setError(msg || 'Une erreur est survenue lors de la connexion. Veuillez réessayer.');
        }
        setLoading(false);
      }
    }
  };

  const proceedWithLogin = async () => {
    setLoading(true);

    try {
      const identifier = loginMethod === 'email' ? email : phone;
      
      const result = await signIn(identifier, password, loginMethod);
      
      if (!result || !result.role) {
        setError('Erreur lors du chargement de votre profil. Veuillez réessayer.');
        setLoading(false);
        return;
      }

      await navigateAfterLogin(result.role);
      
    } catch (err: any) {
      if (err.message?.includes('Invalid login credentials')) {
        setError(loginMethod === 'email' ? 'Email ou mot de passe incorrect' : 'Numéro de téléphone ou mot de passe incorrect');
      } else if (err.message?.includes('profil')) {
        setError(err.message);
      } else {
        setError('Une erreur est survenue lors de la connexion. Veuillez réessayer.');
      }
      
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetMessage('');
    setLoading(true);

    try {
      // On utilise fetch direct pour avoir le corps de la réponse en cas d'erreur
      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-password-reset`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ email: resetEmail, redirectTo: `${window.location.origin}${pathPrefix}` }),
        }
      );

      const body = await res.text();

      let data: any = {};
      try {
        data = JSON.parse(body);
      } catch (e) {
        // Réponse non-JSON
      }

      if (!res.ok) {
        if (data.brevoResponse) {
          setResetMessage(`Erreur Brevo (${data.brevoStatus}): ${data.brevoResponse}`);
        } else if (data.error) {
          setResetMessage(data.debug ? `${data.error} [Debug: ${data.debug}]` : data.error);
        } else if (data.message) {
          setResetMessage(data.message);
        } else {
          setResetMessage(`Erreur serveur (${res.status}). Veuillez réessayer.`);
        }
        return;
      }

      setResetMessage(data.message || 'Un email de réinitialisation a été envoyé à votre adresse.');
      setTimeout(() => {
        setShowResetModal(false);
        setResetEmail('');
        setResetMessage('');
      }, 4000);
    } catch (err: any) {
      setResetMessage('Problème de connexion. Vérifiez votre connexion internet.');
    } finally {
      setLoading(false);
    }
  };

  // Page de vérification email
  if (showEmailVerification) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />

        <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            <div className="mb-6">
              <button
                onClick={() => {
                  setShowEmailVerification(false);
                  setEmailVerificationCode('');
                }}
                className="text-gray-500 hover:text-gray-700 cursor-pointer flex items-center gap-2"
              >
                <i className="ri-arrow-left-line text-xl"></i>
                <span>Retour</span>
              </button>
            </div>
            <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-mail-line text-3xl text-clinic-blue"></i>
                </div>
                <h2 className="text-3xl font-bold text-clinic-blue-dark">Vérification Email</h2>
                <p className="text-gray-600 mt-2">
                  Un code à 6 chiffres a été envoyé à
                </p>
                <p className="text-clinic-blue font-semibold mt-1">{verificationEmail || email}</p>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl text-sm">
                  <div className="flex items-start gap-2">
                    <i className={`${isNetworkError ? 'ri-wifi-off-line' : 'ri-error-warning-line'} text-lg flex-shrink-0 mt-0.5`}></i>
                    <div className="flex-1">
                      <p className="font-medium">{error}</p>
                      {isNetworkError && (
                        <button
                          type="button"
                          onClick={() => {
                            setError('');
                            setIsNetworkError(false);
                            sendEmailVerificationCode();
                          }}
                          className="mt-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-full font-medium text-xs transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-refresh-line mr-1"></i>
                          Réessayer
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <label htmlFor="emailCode" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                    Code de vérification
                  </label>
                  <input
                    id="emailCode"
                    type="text"
                    maxLength={6}
                    value={emailVerificationCode}
                    onChange={(e) => setEmailVerificationCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-center text-2xl font-mono tracking-widest"
                    placeholder="000000"
                  />
                </div>

                <button
                  onClick={verifyEmailCodeAndLogin}
                  disabled={loading || emailVerificationCode.length !== 6}
                  className="w-full px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {loading ? 'Vérification...' : 'Vérifier et se connecter'}
                </button>

                <div className="text-center">
                  {emailResendTimer > 0 ? (
                    <p className="text-gray-600 text-sm">
                      Renvoyer le code dans {emailResendTimer}s
                    </p>
                  ) : (
                    <button
                      onClick={sendEmailVerificationCode}
                      disabled={loading}
                      className="text-clinic-blue hover:text-clinic-blue-dark font-semibold text-sm cursor-pointer"
                    >
                      Renvoyer le code
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Page de vérification téléphone (2FA SMS)
  if (showPhoneVerification) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />

        <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            <div className="mb-6">
              <button
                onClick={() => {
                  setShowPhoneVerification(false);
                  setPhoneVerificationCode('');
                }}
                className="text-gray-500 hover:text-gray-700 cursor-pointer flex items-center gap-2"
              >
                <i className="ri-arrow-left-line text-xl"></i>
                <span>Retour</span>
              </button>
            </div>
            <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-smartphone-line text-3xl text-clinic-blue"></i>
                </div>
                <h2 className="text-3xl font-bold text-clinic-blue-dark">Vérification SMS</h2>
                <p className="text-gray-600 mt-2">
                  Un code à 6 chiffres a été envoyé au
                </p>
                <p className="text-clinic-blue font-semibold mt-1">{phone}</p>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl text-sm">
                  <div className="flex items-start gap-2">
                    <i className={`${isNetworkError ? 'ri-wifi-off-line' : 'ri-error-warning-line'} text-lg flex-shrink-0 mt-0.5`}></i>
                    <div className="flex-1">
                      <p className="font-medium">{error}</p>
                      {isNetworkError && (
                        <button
                          type="button"
                          onClick={() => {
                            setError('');
                            setIsNetworkError(false);
                            sendPhoneVerificationCode();
                          }}
                          className="mt-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-full font-medium text-xs transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-refresh-line mr-1"></i>
                          Réessayer
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <label htmlFor="phoneCode" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                    Code de vérification
                  </label>
                  <input
                    id="phoneCode"
                    type="text"
                    maxLength={6}
                    value={phoneVerificationCode}
                    onChange={(e) => setPhoneVerificationCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-center text-2xl font-mono tracking-widest"
                    placeholder="000000"
                  />
                </div>

                <button
                  onClick={verifyPhoneCodeAndLogin}
                  disabled={loading || phoneVerificationCode.length !== 6}
                  className="w-full px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {loading ? 'Vérification...' : 'Vérifier et se connecter'}
                </button>

                <div className="text-center">
                  {phoneResendTimer > 0 ? (
                    <p className="text-gray-600 text-sm">
                      Renvoyer le code dans {phoneResendTimer}s
                    </p>
                  ) : (
                    <button
                      onClick={sendPhoneVerificationCode}
                      disabled={loading}
                      className="text-clinic-blue hover:text-clinic-blue-dark font-semibold text-sm cursor-pointer"
                    >
                      Renvoyer le code
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          {showSecurityResetBanner && (
            <div className="mb-4 p-4 bg-amber-50 border-2 border-amber-200 text-amber-800 rounded-2xl text-sm flex items-start gap-3">
              <i className="ri-shield-keyhole-line text-xl flex-shrink-0 mt-0.5"></i>
              <div>
                <p className="font-semibold">Réinitialisation de sécurité en cours</p>
                <p className="mt-1">
                  Médecins : connectez-vous avec votre email et le mot de passe temporaire communiqué par l'administration. Vous serez ensuite guidé pour associer votre email personnel et définir un nouveau mot de passe.
                </p>
              </div>
            </div>
          )}
          <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-login-box-line text-3xl text-clinic-blue"></i>
              </div>
              <h2 className="text-3xl font-bold text-clinic-blue-dark">Connexion</h2>
              <p className="text-gray-600 mt-2">Accédez à votre espace personnel</p>
            </div>

            {infoMessage && (
              <div className="mb-4 p-4 bg-amber-50 border-2 border-amber-200 text-amber-800 rounded-2xl text-sm flex items-start gap-3">
                <i className="ri-information-line text-xl flex-shrink-0 mt-0.5"></i>
                <span>{infoMessage}</span>
              </div>
            )}

            {error && (
              <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl text-sm">
                <div className="flex items-start gap-2">
                  <i className={`${isNetworkError ? 'ri-wifi-off-line' : 'ri-error-warning-line'} text-lg flex-shrink-0 mt-0.5`}></i>
                  <div className="flex-1">
                    <p className="font-medium">{error}</p>
                    {isNetworkError && (
                      <>
                        <p className="mt-2 text-red-500 text-sm">
                          Le serveur d'authentification (<code className="bg-red-100 px-1 rounded">{import.meta.env.VITE_PUBLIC_SUPABASE_URL}</code>) est injoignable. 
                          Cela peut arriver si le projet Supabase est en pause (formule gratuite) ou en maintenance.
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setError('');
                            setIsNetworkError(false);
                            handleSubmit({ preventDefault: () => {} } as React.FormEvent);
                          }}
                          className="mt-3 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-full font-medium text-xs transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-refresh-line mr-1"></i>
                          Réessayer
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Bouton de connexion Google */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full mb-6 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 hover:border-gray-400 transition-all font-semibold shadow-md hover:shadow-lg flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuer avec Google
            </button>

            {/* Séparateur */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Ou</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-clinic-blue-dark mb-3">
                  Se connecter avec
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setLoginMethod('email')}
                    className={`flex-1 px-4 py-3 rounded-2xl border-2 transition-all whitespace-nowrap cursor-pointer ${
                      loginMethod === 'email'
                        ? 'bg-clinic-blue text-white border-clinic-blue'
                        : 'bg-white text-gray-700 border-clinic-blue/20 hover:border-clinic-blue/40'
                    }`}
                  >
                    <i className="ri-mail-line mr-2"></i>
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => setLoginMethod('phone')}
                    className={`flex-1 px-4 py-3 rounded-2xl border-2 transition-all whitespace-nowrap cursor-pointer ${
                      loginMethod === 'phone'
                        ? 'bg-clinic-blue text-white border-clinic-blue'
                        : 'bg-white text-gray-700 border-clinic-blue/20 hover:border-clinic-blue/40'
                    }`}
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Téléphone
                  </button>
                </div>
              </div>

              {loginMethod === 'email' ? (
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                    Email
                  </label>
                  <input
                    ref={emailInputRef}
                    id="email"
                    type="email"
                    required
                    autoComplete="off"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    placeholder="votre@email.com"
                  />
                </div>
              ) : (
                <PhoneInput
                  id="phone"
                  name="phone"
                  label="Numéro de téléphone"
                  required
                  value={phone}
                  onChange={(val) => setPhone(val)}
                />
              )}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                  Mot de passe
                </label>
                <div className="relative">
                  <input
                    ref={passwordInputRef}
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="off"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-clinic-blue-dark transition-colors cursor-pointer"
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  >
                    <i className={showPassword ? 'ri-eye-off-line text-lg' : 'ri-eye-line text-lg'}></i>
                  </button>
                </div>
              </div>

              {/* Cloudflare Turnstile - conteneur TOUJOURS rendu pour éviter le deadlock */}
              <div className="flex justify-center min-h-[65px] items-center relative">
                {/* Le container Turnstile - toujours monté, invisible pendant le chargement */}
                <div
                  key={turnstileKey}
                  ref={turnstileContainerRef}
                  className={turnstileReady ? '' : 'opacity-0 absolute pointer-events-none'}
                ></div>

                {/* Spinner pendant le chargement */}
                {!turnstileReady && !turnstileError && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span className="text-sm">Chargement de la vérification...</span>
                  </div>
                )}

                {/* Message d'erreur */}
                {turnstileError && (
                  <div className="text-center py-2">
                    <p className="text-red-500 text-sm mb-2 flex items-center justify-center gap-1">
                      <i className="ri-error-warning-line"></i>
                      La vérification anti-robot (Cloudflare) n'a pas pu se charger. Vérifiez votre connexion internet, puis réessayez.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setTurnstileError(false);
                        setTurnstileReady(false);
                        setTurnstileToken('');
                        turnstileMountedRef.current = false;
                        turnstileWidgetIdRef.current = null;
                        setTurnstileKey(prev => prev + 1);
                      }}
                      className="text-sm text-clinic-blue hover:text-clinic-blue-dark font-semibold underline cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-refresh-line mr-1"></i>
                      Réessayer
                    </button>
                  </div>
                )}
              </div>

              {loginMethod === 'email' && (
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setShowResetModal(true)}
                    className="text-sm text-clinic-blue hover:text-clinic-blue-dark transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !turnstileToken}
                className="w-full px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap cursor-pointer"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Connexion en cours...
                  </span>
                ) : (
                  'Se connecter'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Pas encore de compte ?{' '}
                <Link to="/register" className="text-clinic-blue hover:text-clinic-blue-dark font-semibold transition-colors whitespace-nowrap">
                  Inscrivez-vous
                </Link>
              </p>
              <p className="mt-3 text-sm">
                <Link to="/creer-compte-patient" className="text-clinic-green hover:text-clinic-green/80 font-semibold transition-colors whitespace-nowrap">
                  <i className="ri-key-2-line mr-1"></i>
                  Code d'activation reçu à la clinique ?
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de réinitialisation de mot de passe */}
      {showResetModal && (
        <div key="reset-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-clinic-blue-dark">Réinitialiser le mot de passe</h3>
              <button
                onClick={() => {
                  setShowResetModal(false);
                  setResetMessage('');
                  setResetEmail('');
                }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {resetMessage && (
              <div className="mb-4 p-4 bg-clinic-green/10 border-2 border-clinic-green/30 text-clinic-green rounded-2xl text-sm">
                {resetMessage}
              </div>
            )}

            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                  Adresse email
                </label>
                <input
                  id="reset-email"
                  type="email"
                  required
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                  placeholder="votre@email.com"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {loading ? 'Envoi...' : 'Envoyer le lien'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Modal de compte bloqué */}
      {showBlockedModal && (
        <div key="blocked-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lock-line text-4xl text-red-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-red-600 mb-2">Compte bloqué</h3>
              <p className="text-gray-700">
                Votre compte a été créé par un membre du personnel médical. Vous deviez changer votre mot de passe dans les 24 heures suivant la création.
              </p>
            </div>

            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-6">
              <p className="text-sm text-red-800 font-medium">
                <i className="ri-alert-line mr-2"></i>
                Le délai de 24 heures est dépassé. Pour des raisons de sécurité, votre compte est maintenant bloqué.
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-sm text-gray-600 text-center">
                Veuillez contacter le personnel médical pour réinitialiser votre mot de passe.
              </p>
              
              <button
                onClick={() => {
                  setShowBlockedModal(false);
                  navigate('/contact', { replace: true });
                }}
                className="w-full px-6 py-3 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold whitespace-nowrap cursor-pointer"
              >
                Contacter la clinique
              </button>
              
              <button
                onClick={() => {
                  setShowBlockedModal(false);
                  navigate('/', { replace: true });
                }}
                className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all font-semibold whitespace-nowrap cursor-pointer"
              >
                Retour à l'accueil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal 2FA non configuré (personnel) */}
      {show2faSetupRequired && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-keyhole-line text-4xl text-amber-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-amber-700 mb-2">Vérification 2FA requise</h3>
              <p className="text-gray-700">
                La vérification en deux étapes est désormais obligatoire pour le personnel.
                Votre adresse email 2FA n'a pas encore été configurée.
              </p>
            </div>
            <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 mb-6">
              <p className="text-sm text-amber-800 font-medium">
                <i className="ri-information-line mr-2"></i>
                Veuillez contacter l'administrateur de la clinique pour associer votre email personnel (alias) à votre compte.
              </p>
            </div>
            <button
              onClick={() => {
                setShow2faSetupRequired(false);
                navigate('/', { replace: true });
              }}
              className="w-full px-6 py-3 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-all font-semibold whitespace-nowrap cursor-pointer"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      )}

      {/* Modal de configuration de l'email personnel (réinitialisation sécurité médecins) */}
      {showSecuritySetup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-settings-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-clinic-blue-dark mb-2">Sécuriser votre compte</h3>
              <p className="text-gray-600 text-sm">
                Pour votre sécurité, vous devez associer votre adresse email personnelle à votre compte avant de continuer.
              </p>
            </div>

            {securityError && (
              <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl text-sm">
                {securityError}
              </div>
            )}

            {securitySetupStep === 'enterEmail' ? (
              <div className="space-y-6">
                <div>
                  <label htmlFor="security-personal-email" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                    Votre email personnel
                  </label>
                  <input
                    id="security-personal-email"
                    type="email"
                    value={securityPersonalEmail}
                    onChange={(e) => setSecurityPersonalEmail(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    placeholder="vous@exemple.com"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Un code de vérification sera envoyé à cette adresse pour confirmer qu'elle vous appartient.
                  </p>
                </div>
                <button
                  onClick={sendSecuritySetupCode}
                  disabled={securityLoading || !securityPersonalEmail}
                  className="w-full px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {securityLoading ? 'Envoi...' : 'Envoyer le code'}
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                <p className="text-sm text-gray-600 text-center">
                  Un code à 6 chiffres a été envoyé à <strong>{securityPersonalEmail}</strong>
                </p>
                <div>
                  <label htmlFor="security-code" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                    Code de vérification
                  </label>
                  <input
                    id="security-code"
                    type="text"
                    maxLength={6}
                    value={securityCode}
                    onChange={(e) => setSecurityCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-center text-2xl font-mono tracking-widest"
                    placeholder="000000"
                  />
                </div>
                <button
                  onClick={verifySecuritySetupCode}
                  disabled={securityLoading || securityCode.length !== 6}
                  className="w-full px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {securityLoading ? 'Vérification...' : 'Vérifier et continuer'}
                </button>
                <div className="text-center">
                  {securityResendTimer > 0 ? (
                    <p className="text-gray-600 text-sm">Renvoyer le code dans {securityResendTimer}s</p>
                  ) : (
                    <button
                      onClick={sendSecuritySetupCode}
                      disabled={securityLoading}
                      className="text-clinic-blue hover:text-clinic-blue-dark font-semibold text-sm cursor-pointer"
                    >
                      Renvoyer le code
                    </button>
                  )}
                </div>
                <button
                  onClick={() => { setSecuritySetupStep('enterEmail'); setSecurityCode(''); setSecurityError(''); }}
                  className="w-full text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  Modifier l'email
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}